/**
 * WorkcentersService — phase-1 data layer for the BQC1/BQC3 migration.
 * Reads the legacy contractor roster (read-only, cross-site) and owns the
 * iSyn2_ event lists. Write matrix per docs/bqc-migration/SCHEMA.md — the
 * Excel importer never touches anything this service writes.
 */

import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL, LEGACY_MFG_SITE_URL } from "./mfgSite";
import { CONTRACTORS_LIST, CONTRACTORS_SELECT } from "./contractorsList";
import type { ILegacyContractorRow } from "./contractorsList";
// Re-exported because half the module imports the roster row from here.
export type { ILegacyContractorRow } from "./contractorsList";
export { CONTRACTORS_LIST, CONTRACTORS_SELECT } from "./contractorsList";
import type { ICommitmentRow } from "../../modules/mfg-workcenters/commitments";
import { latestPickPerWip } from "../../modules/mfg-workcenters/queueSource";

export type { ICommitmentRow };

const ASSIGNMENTS_LIST = "iSyn2_Assignments";
const DRYRUN_LIST = "iSyn2_DryRunLog";
const UNAVAILABILITY_LIST = "iSyn2_SewerUnavailability";
const METRICS_LIST = "iSyn2_SewerMetrics";
const COMMITMENTS_LIST = "iSyn2_SewerCommitments";
const CUTREADY_LIST = "iSyn2_CutReadyScans";
/**
 * A LADDER, ONE RUNG PER GENERATION OF COLUMNS — not a pair.
 *
 * SharePoint rejects the WHOLE $select the moment it names one column the
 * list does not have, so every read of this list is only as good as its
 * newest column. A two-rung SELECT/CORE pair looks like it handles that and
 * does not: appending a brand-new column to SELECT means an unprovisioned
 * site drops all the way to CORE, losing `cutter` and `syncedToWorkbookAt`
 * along with the column that was actually missing.
 *
 * That is not hypothetical. cutterSetBy/cutterSetAt went onto SELECT on
 * 2026-09-11 and blanked CUT BY across the whole live BQC2 table — every name
 * this station had ever recorded, gone from the screen, while NEEDS EXPORT
 * climbed because syncedToWorkbookAt had gone with it. Nothing was lost in
 * SharePoint; the app simply could not read the columns any more.
 *
 * So each generation gets its own rung and the read walks down them. A
 * missing column now costs exactly itself and everything newer, never
 * anything that was already working. ADD A NEW RUNG — never widen an
 * existing one — and the next column cannot repeat this.
 */
const CUTREADY_CORE = "Id,wipId,design,sku,qty,scannedBy,scannedAt,undoneBy,undoneAt";
/** + the CUTTING outbox's sync bookkeeping and the cutter picked at the
 * station. Provisioned long since; this is the rung that must keep working. */
const CUTREADY_SELECT = `${CUTREADY_CORE},syncedToWorkbookAt,cutter`;
/** + who filled in a blank cutter after the fact — see nameCutReadyCutter.
 * Newest rung, and the one a site that has not run "Add missing columns"
 * falls off. */
const CUTREADY_AUDIT = `${CUTREADY_SELECT},cutterSetBy,cutterSetAt`;

/** The rungs, newest first — the order listCutReadyScans tries them in. */
const CUTREADY_RUNGS: readonly string[] = [CUTREADY_AUDIT, CUTREADY_SELECT, CUTREADY_CORE];

/** Raw legacy iSyn_Contractors row (internal names incl. x0020 encodings). */
/** What `publishSewerMetrics` writes — the row minus its server-owned fields. */
export type ISewerMetricPublish = Omit<ISewerMetricRow, "spId" | "computedAt" | "computedBy">;

export interface IAssignmentEvent {
  spId: number;
  wipId: string;
  sewer: string;
  assignedBy: string;
  assignedAt: string;
  source: "SMART" | "MANUAL" | "REASSIGN";
  scoreTotal?: number;
  scoreReason?: string;
}

export interface IUnavailabilityWindow {
  spId: number;
  sewer: string;
  fromDate: string;
  untilDate: string | null;
  reason: "HEALTH" | "ABSENT" | "OTHER";
  note?: string;
  loggedBy: string;
  loggedAt: string;
  clearedBy?: string;
  clearedAt?: string;
}

export interface ISewerMetricRow {
  spId: number;
  sewer: string;
  avgDaysPerSet: number | null;
  avgDaysSample: number;
  /** Whole-delivery turnaround, undivided — the grading sheet's PROCESSING
   * TIME input. Null on a row published before this column existed. */
  avgDaysPerBundle: number | null;
  monthCapacity: number | null;
  monthsObserved: number;
  quality: number | null;
  qualitySample: number;
  historicalSets: number;
  completedCount: number;
  /** BQC5 repair events; null when the snapshot carried no repair evidence. */
  repairCount: number | null;
  repairSets: number | null;
  /** The subcontractor grade this snapshot computed — 0-100, band, tier.
   * Stored so a published snapshot is a readable scorecard on its own rather
   * than five inputs somebody has to re-run the formula over. */
  gradeScore: number | null;
  grade: string;
  tier: string;
  computedAt: string;
  computedBy: string;
}

/**
 * One WIP scanned cut-ready at the BQC3 station. This log — not the
 * workbook's BQC2 stage — is what feeds the Assignment Board's Cut · ready
 * queue: a bundle enters the queue when someone physically holds it under
 * the scanner, which is what makes the queue real-time and accurate.
 *
 * Like commitments, this is app-owned intake the Excel importer never
 * touches, so recording a scan is safe at every valve stage — it is an
 * observation about the physical floor, not an assignment.
 */
export interface ICutReadyScan {
  spId: number;
  wipId: string;
  design: string;
  sku: string;
  qty: number;
  scannedBy: string;
  scannedAt: string;
  /** Who physically cut the bundle, chosen at the station — distinct from
   * scannedBy, which is whoever was operating the scanner. The workbook's own
   * equivalent is the CUTTING sheet's CUTTER 1/2/3 columns; they sit outside
   * the range AutoSyncBQC imports, so this is the app's only record of it.
   * Absent on a scan taken before the field existed, and on any read that had
   * to fall back to CUTREADY_CORE. */
  cutter?: string;
  /** Who named the cutter AFTER the scan was taken, and when.
   *
   * Absent on an ordinary BQC2 scan: there the cutter is picked before the
   * bundle is scanned, so `scannedBy` already says who was at the table.
   * Present only where somebody filled in a blank — today that means a scan
   * Dispatch·BQC3 created for a bundle with no cut scan of its own. Kept
   * separate from `cutter` so a name typed later is not read back as one
   * picked at the moment of cutting. */
  cutterSetBy?: string;
  cutterSetAt?: string;
  /** Set when the scan was undone (mis-scan). Soft — the row stays as history. */
  undoneBy?: string;
  undoneAt?: string;
  /** Last time this scan was folded into the CUTTING outbox CSV that
   * AutoWriteCutting.bas merges into the legacy CUTTING USERFORM.xlsb
   * workbook's own Cutting sheet — see cuttingOutbox.ts. Absent means never
   * exported. Stamped per SCAN, not per WIP: the Cutting sheet's QTY is a
   * running total across a WIP's active scans, so a later second sitting must
   * show up as "not yet synced" on its own even though earlier scans for the
   * same WIP already were. Same meaning and mechanics as
   * IReceivingScan.syncedToWorkbookAt. */
  syncedToWorkbookAt?: string;
}

/**
 * One row of the Gate-1 intent log. At the dry-run valve stage the board's
 * Apply writes ONLY here, so this is the record a station screen reads to
 * show what the planner intends — a proposal, never an assignment, and the
 * distinction must survive all the way to the pixel that renders it.
 */
export interface IDryRunPick {
  spId: number;
  wipId: string;
  proposedSewer: string;
  runAt: string;
  runBy: string;
}

export interface INewAssignment {
  wipId: string;
  sewer: string;
  sewerCode?: string;
  source: "SMART" | "MANUAL" | "REASSIGN";
  scoreTotal?: number;
  scoreReason?: string;
  batchCode?: string;
  design?: string;
  qty?: number;
}

export class WorkcentersService {
  private readonly mfg = createSharePointClient(MFG_SITE_URL);
  private readonly legacy = createSharePointClient(LEGACY_MFG_SITE_URL);
  private entityTypes = new Map<string, string>();
  /** Set by loadRoster — see rosterSource. */
  private rosterSiteUrl: string | null = null;
  private rosterFallbackReason: string | null = null;

  private async entityType(list: string): Promise<string> {
    const hit = this.entityTypes.get(list);
    if (hit) return hit;
    const res = await this.mfg.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${list}')?$select=ListItemEntityTypeFullName`
    );
    this.entityTypes.set(list, res.d.ListItemEntityTypeFullName);
    return res.d.ListItemEntityTypeFullName;
  }

  /**
   * The contractor roster, from MANUFACTURING first and the old site only if
   * it has to be.
   *
   * WHY THIS IS NOT SIMPLY "READ MANUFACTURING". The roster is the one list
   * this module still reads off the old i-SYNERGYTest site, and that split is
   * what makes a name go missing in a way nobody can explain: somebody
   * registers a sewer, looks for them in Manufacturing where every other list
   * lives, and finds nothing — because the list the app actually reads is on
   * a different site. Manufacturing is where it belongs, so that is what is
   * asked first. Copy it across with Migration Valve → "Contractors → the
   * Manufacturing site"; until that has been run, the old site still answers
   * and nothing on the floor changes.
   *
   * AN EMPTY LIST IS NOT AN ANSWER. A roster of zero is never a real state —
   * it is a list that was created but not yet filled, half a copy, or a view
   * problem — and taking it at face value would empty every ranking on every
   * screen at once. So zero rows falls back exactly like a missing list does.
   *
   * STILL FAILS CLOSED (Gate 0.2). The fallback substitutes the SAME list on
   * the site it used to live on, never a built-in roster; if that read fails
   * too this throws, and every caller disables Smart Assign as before.
   */
  public async loadRoster(): Promise<ILegacyContractorRow[]> {
    type Client = ReturnType<typeof createSharePointClient>;
    const read = (client: Client): Promise<ILegacyContractorRow[]> =>
      client.getListItems<ILegacyContractorRow>(CONTRACTORS_LIST, { select: CONTRACTORS_SELECT });

    try {
      const rows = await read(this.mfg);
      if (rows.length > 0) {
        this.rosterSiteUrl = MFG_SITE_URL;
        return rows;
      }
      this.rosterFallbackReason = `${CONTRACTORS_LIST} on the Manufacturing site is empty`;
    } catch (e) {
      // Any failure here falls back rather than only a missing list: the old
      // site holds the same rows, so a bad $select, a permission gap or a
      // dropped request costs nothing but a second request. What must NOT
      // happen is the floor losing its roster over a list that is still
      // being set up.
      this.rosterFallbackReason = e instanceof Error ? e.message : String(e);
    }

    const rows = await read(this.legacy);
    this.rosterSiteUrl = LEGACY_MFG_SITE_URL;
    return rows;
  }

  /** Which site answered the last roster read, and why it was not the first
   *  choice. For the diagnostics screen — a roster nobody can find is exactly
   *  the question this answers. */
  public rosterSource(): { site: string | null; fellBackBecause: string | null } {
    return { site: this.rosterSiteUrl, fellBackBecause: this.rosterFallbackReason };
  }

  /** All assignment events; callers reduce to latest-per-wip. */
  public async listAssignmentEvents(): Promise<IAssignmentEvent[]> {
    const rows = await this.mfg.getListItems<Record<string, unknown>>(ASSIGNMENTS_LIST, {
      select: "Id,wipId,sewer,assignedBy,assignedAt,source,scoreTotal,scoreReason",
    });
    return rows.map(r => ({
      spId: r.Id as number,
      wipId: String(r.wipId ?? ""),
      sewer: String(r.sewer ?? ""),
      assignedBy: String(r.assignedBy ?? ""),
      assignedAt: String(r.assignedAt ?? ""),
      source: (r.source as IAssignmentEvent["source"]) ?? "MANUAL",
      scoreTotal: typeof r.scoreTotal === "number" ? r.scoreTotal : undefined,
      scoreReason: r.scoreReason ? String(r.scoreReason) : undefined,
    }));
  }

  /** Latest assignment per wipId (ties broken by higher Id). */
  public static latestPerWip(events: IAssignmentEvent[]): Map<string, IAssignmentEvent> {
    const map = new Map<string, IAssignmentEvent>();
    for (const e of events) {
      const prev = map.get(e.wipId);
      if (!prev || e.assignedAt > prev.assignedAt || (e.assignedAt === prev.assignedAt && e.spId > prev.spId)) {
        map.set(e.wipId, e);
      }
    }
    return map;
  }

  /** Sequential writes (SP throttle discipline). Returns per-WIP outcome. */
  public async createAssignments(
    rows: INewAssignment[],
    assignedBy: string,
    onProgress?: (done: number, total: number) => void
  ): Promise<{ ok: string[]; failed: { wipId: string; error: string }[] }> {
    const type = await this.entityType(ASSIGNMENTS_LIST);
    const ok: string[] = [];
    const failed: { wipId: string; error: string }[] = [];
    for (let i = 0; i < rows.length; i++) {
      const a = rows[i];
      try {
        await this.mfg.createItem(ASSIGNMENTS_LIST, {
          __metadata: { type },
          Title: a.wipId,
          wipId: a.wipId,
          sewer: a.sewer,
          sewerCode: a.sewerCode ?? "",
          assignedBy,
          assignedAt: new Date().toISOString(),
          source: a.source,
          scoreTotal: a.scoreTotal ?? null,
          scoreReason: a.scoreReason ?? "",
          batchCode: a.batchCode ?? "",
          design: a.design ?? "",
          qty: a.qty ?? null,
        });
        ok.push(a.wipId);
      } catch (e) {
        failed.push({ wipId: a.wipId, error: e instanceof Error ? e.message : String(e) });
      }
      onProgress?.(i + 1, rows.length);
    }
    return { ok, failed };
  }

  /** Dry-run mode writes HERE and nowhere else (Gate 1.1). */
  public async logDryRunPicks(
    picks: { wipId: string; proposedSewer: string; scoreTotal: number; scoreReason?: string; alternatives?: { sewer: string; score: number }[] }[],
    runBy: string,
    onProgress?: (done: number, total: number) => void
  ): Promise<{ ok: number; failed: number }> {
    const type = await this.entityType(DRYRUN_LIST);
    let ok = 0, failed = 0;
    for (let i = 0; i < picks.length; i++) {
      const p = picks[i];
      try {
        await this.mfg.createItem(DRYRUN_LIST, {
          __metadata: { type },
          Title: p.wipId,
          wipId: p.wipId,
          proposedSewer: p.proposedSewer,
          scoreTotal: p.scoreTotal,
          scoreReason: p.scoreReason ?? "",
          alternatives: JSON.stringify(p.alternatives ?? []),
          runAt: new Date().toISOString(),
          runBy,
        });
        ok++;
      } catch {
        failed++;
      }
      onProgress?.(i + 1, picks.length);
    }
    return { ok, failed };
  }

  /** Derived scoring inputs, app-owned. Empty (not an error) before the first
   * publish — callers fall back to the legacy roster's own values. */
  public async listSewerMetrics(): Promise<ISewerMetricRow[]> {
    const rows = await this.mfg.getListItems<Record<string, unknown>>(METRICS_LIST, {
      select:
        "Id,sewer,avgDaysPerSet,avgDaysSample,avgDaysPerBundle,monthCapacity,monthsObserved,quality,qualitySample," +
        "historicalSets,completedCount,repairCount,repairSets,gradeScore,grade,tier,computedAt,computedBy",
    });
    const numOrNull = (v: unknown): number | null => (typeof v === "number" ? v : null);
    return rows.map(r => ({
      spId: r.Id as number,
      sewer: String(r.sewer ?? "").toUpperCase().trim(),
      avgDaysPerSet: numOrNull(r.avgDaysPerSet),
      avgDaysSample: Number(r.avgDaysSample ?? 0),
      avgDaysPerBundle: numOrNull(r.avgDaysPerBundle),
      monthCapacity: numOrNull(r.monthCapacity),
      monthsObserved: Number(r.monthsObserved ?? 0),
      quality: numOrNull(r.quality),
      qualitySample: Number(r.qualitySample ?? 0),
      historicalSets: Number(r.historicalSets ?? 0),
      completedCount: Number(r.completedCount ?? 0),
      repairCount: numOrNull(r.repairCount),
      repairSets: numOrNull(r.repairSets),
      gradeScore: numOrNull(r.gradeScore),
      grade: String(r.grade ?? ""),
      tier: String(r.tier ?? ""),
      computedAt: String(r.computedAt ?? ""),
      computedBy: String(r.computedBy ?? ""),
    }));
  }

  /** Upsert one row per sewer (recompute overwrites in place — this is a
   * derived projection, not an event log, so history isn't meaningful here). */
  public async publishSewerMetrics(
    metrics: ISewerMetricPublish[],
    computedBy: string,
    onProgress?: (done: number, total: number) => void
  ): Promise<{ ok: number; failed: number }> {
    const type = await this.entityType(METRICS_LIST);
    const existing = new Map((await this.listSewerMetrics()).map(m => [m.sewer, m.spId]));
    let ok = 0, failed = 0;
    for (let i = 0; i < metrics.length; i++) {
      const m = metrics[i];
      const body = {
        __metadata: { type },
        Title: m.sewer,
        sewer: m.sewer,
        avgDaysPerSet: m.avgDaysPerSet,
        avgDaysSample: m.avgDaysSample,
        avgDaysPerBundle: m.avgDaysPerBundle,
        monthCapacity: m.monthCapacity,
        monthsObserved: m.monthsObserved,
        quality: m.quality,
        qualitySample: m.qualitySample,
        historicalSets: m.historicalSets,
        completedCount: m.completedCount,
        repairCount: m.repairCount,
        repairSets: m.repairSets,
        gradeScore: m.gradeScore,
        grade: m.grade,
        tier: m.tier,
        computedAt: new Date().toISOString(),
        computedBy,
      };
      try {
        const spId = existing.get(m.sewer);
        if (spId) await this.mfg.updateItem(METRICS_LIST, spId, body);
        else await this.mfg.createItem(METRICS_LIST, body);
        ok++;
      } catch { failed++; }
      onProgress?.(i + 1, metrics.length);
    }
    return { ok, failed };
  }

  public async listUnavailability(): Promise<IUnavailabilityWindow[]> {
    const rows = await this.mfg.getListItems<Record<string, unknown>>(UNAVAILABILITY_LIST, {
      select: "Id,sewer,fromDate,untilDate,reason,note,loggedBy,loggedAt,clearedBy,clearedAt",
    });
    return rows.map(r => ({
      spId: r.Id as number,
      sewer: String(r.sewer ?? ""),
      fromDate: String(r.fromDate ?? ""),
      untilDate: r.untilDate ? String(r.untilDate) : null,
      reason: (r.reason as IUnavailabilityWindow["reason"]) ?? "OTHER",
      note: r.note ? String(r.note) : undefined,
      loggedBy: String(r.loggedBy ?? ""),
      loggedAt: String(r.loggedAt ?? ""),
      clearedBy: r.clearedBy ? String(r.clearedBy) : undefined,
      clearedAt: r.clearedAt ? String(r.clearedAt) : undefined,
    }));
  }

  public async listCommitments(): Promise<ICommitmentRow[]> {
    const rows = await this.mfg.getListItems<Record<string, unknown>>(COMMITMENTS_LIST, {
      select: "Id,sewer,weekStart,days,note,committedBy,committedAt,clearedBy,clearedAt",
    });
    return rows.map(r => ({
      spId: r.Id as number,
      sewer: String(r.sewer ?? ""),
      weekStart: String(r.weekStart ?? ""),
      days: String(r.days ?? "0000000"),
      note: r.note ? String(r.note) : undefined,
      committedBy: String(r.committedBy ?? ""),
      committedAt: String(r.committedAt ?? ""),
      clearedBy: r.clearedBy ? String(r.clearedBy) : undefined,
      clearedAt: r.clearedAt ? String(r.clearedAt) : undefined,
    }));
  }

  /**
   * Upsert one row per (sewer, week), the same idiom publishSewerMetrics uses.
   *
   * A commitment is a standing fact — "these are the days I work" — not an
   * event, so replaying every edit as a new row would grow the list without
   * telling anyone anything they act on. Withdrawals soft-clear instead of
   * deleting, which keeps "took the week back" distinguishable from "never
   * filled it in".
   *
   * @param slots  each with spId when a row already exists for that sewer-week
   */
  public async saveCommitments(
    slots: { spId?: number; sewer: string; weekStartIso: string; days: string; note?: string }[],
    committedBy: string,
    onProgress?: (done: number, total: number) => void
  ): Promise<{ ok: number; failed: number; firstError: string | null }> {
    const type = await this.entityType(COMMITMENTS_LIST);
    let ok = 0, failed = 0;
    let firstError: string | null = null;
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      const cleared = /^0{7}$/.test(s.days);
      const body: Record<string, unknown> = {
        __metadata: { type },
        Title: s.sewer,
        sewer: s.sewer,
        weekStart: s.weekStartIso,
        days: s.days,
        note: s.note ?? "",
        committedBy,
        committedAt: new Date().toISOString(),
        clearedBy: cleared ? committedBy : null,
        clearedAt: cleared ? new Date().toISOString() : null,
      };
      try {
        if (s.spId) await this.mfg.updateItem(COMMITMENTS_LIST, s.spId, body);
        else if (!cleared) await this.mfg.createItem(COMMITMENTS_LIST, body);
        ok++;
      } catch (e) {
        failed++;
        firstError ??= e instanceof Error ? e.message : String(e);
      }
      onProgress?.(i + 1, slots.length);
    }
    return { ok, failed, firstError };
  }

  /** One intended pick from a dry-run Apply on the Assignment Board. */
  public async listDryRunPicks(): Promise<IDryRunPick[]> {
    const rows = await this.mfg.getListItems<Record<string, unknown>>(DRYRUN_LIST, {
      select: "Id,wipId,proposedSewer,runAt,runBy",
    });
    return rows.map(r => ({
      spId: r.Id as number,
      wipId: String(r.wipId ?? "").trim(),
      proposedSewer: String(r.proposedSewer ?? "").trim(),
      runAt: String(r.runAt ?? ""),
      runBy: String(r.runBy ?? ""),
    }));
  }

  /**
   * Delete intent-log rows.
   *
   * The log is a rehearsal artefact, not history: at dry-run the board writes
   * proposals here instead of assigning, and a station screen then shows those
   * names in its SEWER column. Left behind after a simulation they read as
   * real assignments — a bundle nobody has dispatched arrives at the dispatch
   * station with somebody's name on it — so clearing them has to be as easy as
   * making them.
   *
   * Best-effort per row: one failure must not strand the rest, and the caller
   * gets the counts to report.
   */
  public async deleteDryRunPicks(
    spIds: number[],
    onProgress?: (done: number, total: number) => void
  ): Promise<{ deleted: number; failed: number; error?: string }> {
    let deleted = 0, failed = 0;
    let error: string | undefined;
    for (let i = 0; i < spIds.length; i++) {
      try { await this.mfg.deleteItem(DRYRUN_LIST, spIds[i]); deleted++; }
      catch (e) {
        failed++;
        // Keep the first reason. A swallowed 403 on this list looks exactly
        // like a clear that worked and then undid itself on refresh, which is
        // the least debuggable thing a button can do.
        if (!error) error = e instanceof Error ? e.message : String(e);
      }
      onProgress?.(i + 1, spIds.length);
    }
    return { deleted, failed, error };
  }

  /** Latest proposal per wipId (ties broken by higher Id) — same reduction
   * latestPerWip applies to real assignment events. */
  public static latestPickPerWip(picks: IDryRunPick[]): Map<string, IDryRunPick> {
    return latestPickPerWip(picks);
  }

  /**
   * Every scan row, undone ones included — the caller reduces to active.
   *
   * Reads with CUTREADY_SELECT, falling back to CUTREADY_CORE on any failure
   * — the same SELECT/CORE pattern ProductionGridService.readWipRows and
   * DispatchService already use, and for the same reason. SharePoint rejects
   * the WHOLE $select the moment it names one column the list does not have,
   * so shipping syncedToWorkbookAt ahead of "Add missing columns" actually
   * being run took out every reader of this list at once: Cutting·BQC2,
   * Dispatch·BQC3's cut-ready queue, the Assignment Board, and — worst,
   * because they swallow the error — the ASSIGNING/RECEIVING/QC outboxes,
   * whose DATE ASSIGNED column reads from these scans and would have gone out
   * blank.
   *
   * Unconditional fallback rather than pattern-matching SharePoint's exact
   * wording, same call the two services above make. syncedToWorkbookAt simply
   * comes back undefined until the column exists, which reads as "never
   * exported" — the safe direction: a scan looks due again rather than
   * silently synced.
   */
  public async listCutReadyScans(): Promise<ICutReadyScan[]> {
    let rows: Record<string, unknown>[] | null = null;
    let last: unknown;
    // Down the ladder one rung at a time — see CUTREADY_RUNGS. Stops at the
    // first rung the list can actually serve, so a column this site has not
    // been given yet costs that column and nothing that came before it.
    for (const select of CUTREADY_RUNGS) {
      try {
        rows = await this.mfg.getListItems<Record<string, unknown>>(CUTREADY_LIST, { select });
        break;
      } catch (e) {
        last = e;
        console.warn(
          `[WorkcentersService] cut-ready scan read refused with select "${select}" — run "Add missing ` +
          "columns\" on the Migration Valve screen. Falling back to the previous generation of columns; " +
          "anything newer reads as absent (a scan with no stamp looks never-exported, which is the safe " +
          "direction, and one with no cutter reads as unnamed).",
          e
        );
      }
    }
    // Every rung refused, CORE included: this is not a missing column, it is
    // the list itself. Throwing is right — the callers that swallow a failure
    // treat an empty list as "no scans", which on the outbox path means
    // exports going out with blank columns and saying nothing.
    if (!rows) throw last instanceof Error ? last : new Error(String(last));
    return rows.map(r => ({
      spId: r.Id as number,
      wipId: String(r.wipId ?? "").trim(),
      design: String(r.design ?? ""),
      sku: String(r.sku ?? ""),
      qty: Number(r.qty ?? 0),
      scannedBy: String(r.scannedBy ?? ""),
      scannedAt: String(r.scannedAt ?? ""),
      cutter: r.cutter ? String(r.cutter) : undefined,
      cutterSetBy: r.cutterSetBy ? String(r.cutterSetBy) : undefined,
      cutterSetAt: r.cutterSetAt ? String(r.cutterSetAt) : undefined,
      undoneBy: r.undoneBy ? String(r.undoneBy) : undefined,
      undoneAt: r.undoneAt ? String(r.undoneAt) : undefined,
      syncedToWorkbookAt: r.syncedToWorkbookAt ? String(r.syncedToWorkbookAt) : undefined,
    }));
  }

  /** Records one scan and returns it with its list id, so the station can
   * show the row (and undo it) without a full reload. */
  public async createCutReadyScan(
    row: { wipId: string; design?: string; sku?: string; qty?: number; cutter?: string },
    scannedBy: string
  ): Promise<ICutReadyScan> {
    const type = await this.entityType(CUTREADY_LIST);
    const scannedAt = new Date().toISOString();
    const created = await this.mfg.createItem<{ Id: number }>(CUTREADY_LIST, {
      __metadata: { type },
      Title: row.wipId,
      wipId: row.wipId,
      design: row.design ?? "",
      sku: row.sku ?? "",
      qty: row.qty ?? null,
      cutter: row.cutter ?? "",
      scannedBy,
      scannedAt,
    });
    return {
      spId: created.Id, wipId: row.wipId, design: row.design ?? "", sku: row.sku ?? "",
      qty: row.qty ?? 0, cutter: row.cutter, scannedBy, scannedAt,
    };
  }

  /** Soft-undo a mis-scan. The row keeps existing as history; it just stops
   * counting as cut-ready everywhere. */
  public async undoCutReadyScan(spId: number, by: string): Promise<void> {
    const type = await this.entityType(CUTREADY_LIST);
    await this.mfg.updateItem(CUTREADY_LIST, spId, {
      __metadata: { type },
      undoneBy: by,
      undoneAt: new Date().toISOString(),
    });
  }

  /**
   * Names the cutter on scans that reached the log without one.
   *
   * WHY THIS WRITE EXISTS AT ALL. Cutting·BQC2 refuses a scan until CUT BY is
   * picked, so a scan it takes always carries a name. Dispatch·BQC3 creates a
   * cut-ready scan when it sends a bundle that has none, and it cannot know
   * who cut it — that is where the blanks come from. Without this the name
   * could never be supplied, the CUT BY column stayed "—" forever, and those
   * sets sat under UNATTRIBUTED on the Cutter output board.
   *
   * ONLY EVER FILLS A BLANK. The caller passes the scans it found with no
   * cutter; nothing here can overwrite a name already recorded at the table.
   *
   * DEGRADES TO THE NAME ALONE. cutterSetBy/cutterSetAt are newer than
   * `cutter`, so on a tenant where "Add missing columns" has not been run yet
   * the full PATCH is rejected. Rather than refuse the edit — which would make
   * the whole button dead until somebody visits the Migration Valve — the
   * write is retried with just `cutter`, and `audited` comes back false so the
   * caller can say the name landed but the note of who typed it did not. Same
   * spirit as listCutReadyScans' own SELECT/CORE fallback.
   *
   * NEVER THROWS FOR A PARTIAL. A WIP cut over two sittings is two rows, and
   * failing the second after the first has been written would leave the caller
   * unable to say which. The ids that were written come back, and `failed`
   * carries the reason the rest were not, so the screen can update exactly the
   * rows that changed and still tell the truth about the others.
   */
  public async nameCutReadyCutter(
    spIds: readonly number[],
    cutter: string,
    by: string
  ): Promise<{ written: number[]; cutterSetAt: string; audited: boolean; failed?: string }> {
    const type = await this.entityType(CUTREADY_LIST);
    const cutterSetAt = new Date().toISOString();
    const written: number[] = [];
    let audited = true;
    for (const spId of spIds) {
      try {
        if (audited) {
          try {
            await this.mfg.updateItem(CUTREADY_LIST, spId, {
              __metadata: { type }, cutter, cutterSetBy: by, cutterSetAt,
            });
            written.push(spId);
            continue;
          } catch (e) {
            console.warn(
              "[WorkcentersService] cut-ready cutter named without cutterSetBy/cutterSetAt — run " +
              "\"Add missing columns\" on the Migration Valve screen to record who filled it in.",
              e
            );
            audited = false;
          }
        }
        await this.mfg.updateItem(CUTREADY_LIST, spId, { __metadata: { type }, cutter });
        written.push(spId);
      } catch (e) {
        return { written, cutterSetAt, audited, failed: e instanceof Error ? e.message : String(e) };
      }
    }
    return { written, cutterSetAt, audited };
  }

  /**
   * Stamps scans as folded into the CUTTING outbox CSV — see
   * ICutReadyScan.syncedToWorkbookAt and cuttingOutbox.ts.
   *
   * One PATCH per row, same as ReceivingService.markSyncedToWorkbook: a batch
   * $batch request would be faster, but a partial failure inside one would
   * leave the caller unable to say which rows actually got stamped, and a
   * wrongly-stamped scan is a scan that silently never reaches the workbook.
   * Throwing here is safe — the CSV is already uploaded, and an unstamped
   * scan simply looks due again on the next export, which is harmless.
   */
  public async markCutReadySyncedToWorkbook(spIds: readonly number[], at: string): Promise<void> {
    const type = await this.entityType(CUTREADY_LIST);
    for (const spId of spIds) {
      await this.mfg.updateItem(CUTREADY_LIST, spId, { __metadata: { type }, syncedToWorkbookAt: at });
    }
  }

  /** Hard-delete a scan row — the record is GONE, not history. For rows that
   * should never have existed (test rehearsal data, junk scans); an ordinary
   * mis-scan wants undoCutReadyScan instead. */
  public async deleteCutReadyScan(spId: number): Promise<void> {
    await this.mfg.deleteItem(CUTREADY_LIST, spId);
  }

  /** Sewers excluded from ranking right now (SCHEMA §3 rule). */
  public static activeUnavailable(windows: IUnavailabilityWindow[], now = new Date()): Map<string, IUnavailabilityWindow> {
    const active = new Map<string, IUnavailabilityWindow>();
    for (const w of windows) {
      if (w.clearedAt) continue;
      const from = new Date(w.fromDate);
      const until = w.untilDate ? new Date(w.untilDate) : null;
      if (now >= from && (until === null || now < until)) active.set(w.sewer.toUpperCase().trim(), w);
    }
    return active;
  }
}

export const workcentersService = new WorkcentersService();
